import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import {
  initialPlaybackLifecycle,
  PlaybackLifecycleEvent,
  playbackLifecycleReducer,
} from "./lifecycle";
import { usePlayerSourceGeneration } from "./usePlayerSourceGeneration";
import { evaluateProgressReport } from "./progressReporting";
import type { ServerSeekIntent } from "./streamPlan";

/**
 * Behavior-preserving facade for state shared by transport, decoder, session,
 * and presentation layers. Commands will move behind this boundary as the
 * coordinator migration proceeds; views should not add new ownership refs.
 */
export function usePlayerController(
  scopeKey: string,
  explicitSourceId?: string,
) {
  const [lifecycle, reducerDispatch] = useReducer(
    playbackLifecycleReducer,
    initialPlaybackLifecycle,
  );
  const lifecycleRef = useRef(lifecycle);
  lifecycleRef.current = lifecycle;

  const positionRef = useRef(initialPlaybackLifecycle.position);
  const pausedRef = useRef(initialPlaybackLifecycle.paused);
  const mutedRef = useRef(initialPlaybackLifecycle.muted);
  const playbackStartedRef = useRef(false);
  const reportScopeRef = useRef(scopeKey);
  const lastProgressReportRef = useRef(0);
  const [serverSeek, setServerSeek] = useState<ServerSeekIntent | null>(null);
  const sourceGeneration = usePlayerSourceGeneration(scopeKey, explicitSourceId);

  if (reportScopeRef.current !== scopeKey) {
    reportScopeRef.current = scopeKey;
    lastProgressReportRef.current = 0;
  }
  useEffect(() => {
    setServerSeek((current) => current?.itemId === scopeKey ? current : null);
  }, [scopeKey]);

  const dispatchLifecycle = useCallback((event: PlaybackLifecycleEvent) => {
    // Compute eagerly so engine callbacks and reporting observe the same state
    // during the interval before React commits the next render.
    const next = playbackLifecycleReducer(lifecycleRef.current, event);
    lifecycleRef.current = next;
    positionRef.current = next.position;
    pausedRef.current = next.paused;
    mutedRef.current = next.muted;
    reducerDispatch(event);
  }, []);

  const markProgressReported = useCallback((seconds: number) => {
    if (Number.isFinite(seconds)) {
      lastProgressReportRef.current = Math.max(0, seconds);
    }
  }, []);

  const shouldReportProgress = useCallback((seconds: number, intervalSeconds = 10) => {
    const decision = evaluateProgressReport(
      lastProgressReportRef.current,
      seconds,
      intervalSeconds,
    );
    lastProgressReportRef.current = decision.nextReportedSeconds;
    return decision.report;
  }, []);

  const prepareTrackChange = useCallback((
    onlyIfDecoderReady: boolean,
    managedHls: boolean,
  ) => {
    const positionSeconds = positionRef.current;
    const prepared = sourceGeneration.prepareTrackChange(
      positionSeconds,
      onlyIfDecoderReady,
    );
    if (managedHls && prepared) {
      setServerSeek({ itemId: scopeKey, seconds: positionSeconds });
    }
    return prepared;
  }, [scopeKey, sourceGeneration.prepareTrackChange]);

  const snapshot = {
    lifecycle,
    position: lifecycle.position,
    duration: lifecycle.duration,
    buffered: lifecycle.buffered,
    paused: lifecycle.paused,
    muted: lifecycle.muted,
    volume: lifecycle.volume,
    serverSeek,
    source: {
      selectedSourceId: sourceGeneration.selectedSourceId,
      surfaceEpoch: sourceGeneration.surfaceEpoch,
      recovering: sourceGeneration.recovering,
    },
  } as const;

  const engineState = {
    positionRef,
    pausedRef,
    mutedRef,
    playbackStartedRef,
  } as const;

  const sourcePort = {
    setSelectedSourceId: sourceGeneration.setSelectedSourceId,
    bindSurface: sourceGeneration.bindSurface,
    isActiveSurface: sourceGeneration.isActiveSurface,
    beginSource: sourceGeneration.beginSource,
    retry: sourceGeneration.retry,
    ready: sourceGeneration.ready,
    acceptProgress: sourceGeneration.acceptProgress,
    markPlaying: sourceGeneration.markPlaying,
    recover: sourceGeneration.recover,
  } as const;

  return {
    snapshot,
    engineState,
    sourcePort,
    lifecycleRef,
    dispatchLifecycle,
    setServerSeek,
    markProgressReported,
    shouldReportProgress,
    prepareTrackChange,
  };
}
